import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { navigationService } from '@/services/navigationService';
import { NavigationItem } from '@/types/navigation';
import { NavigationDebug } from '@/components/navigation/NavigationDebug';

export const NavigationTestPage: React.FC = () => {
  useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NavigationItem[]>([]);
  const [favorites, setFavorites] = useState<NavigationItem[]>([]);
  const [recentItems, setRecentItems] = useState<NavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadNavigationData();
  }, []);

  const loadNavigationData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [favoritesData, recentData] = await Promise.all([
        navigationService.getFavorites(),
        navigationService.getRecentNavigation()
      ]);
      
      setFavorites(favoritesData.favorites);
      setRecentItems(recentData.recent_items);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const searchData = await navigationService.searchNavigationItems(query);
      setSearchResults(searchData.items);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFavorite = async (item: NavigationItem) => {
    try {
      await navigationService.addFavorite({
        [`navigation_${item.item_type}_id`]: item.id
      });
      await loadNavigationData(); // Reload favorites
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveFavorite = async (itemId: string) => {
    try {
      await navigationService.removeFavorite(itemId);
      await loadNavigationData(); // Reload favorites
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Navigation System Test</h1>
      
      <NavigationDebug />
      
      {error && (
        <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 text-[var(--color-error)] px-4 py-3 rounded mb-4">
          Error: {error}
        </div>
      )}

      {/* Search Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-[var(--color-surface-text)]">Search Navigation</h2>
        <div className="flex space-x-4">
          <input
            type="text"
            placeholder="Search navigation items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          />
          <button
            onClick={() => handleSearch(searchQuery)}
            disabled={isLoading}
            className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90 disabled:opacity-50"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        
        {searchResults.length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2 text-[var(--color-surface-text)]">Search Results ({searchResults.length})</h3>
            <div className="space-y-2">
              {searchResults.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-[var(--color-secondary-bg)] rounded-md border border-[var(--color-border)]">
                  <div>
                    <div className="font-medium text-[var(--color-surface-text)]">{item.display_name}</div>
                    <div className="text-sm text-[var(--color-secondary-text)]">{item.route_path}</div>
                    <div className="text-xs text-[var(--color-secondary-text)] opacity-75">Type: {item.item_type}</div>
                  </div>
                  <button
                    onClick={() => handleAddFavorite(item)}
                    className="px-3 py-1 bg-[var(--color-success)] text-[var(--color-accent-text)] rounded text-sm hover:opacity-90"
                  >
                    Add to Favorites
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Favorites Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-[var(--color-surface-text)]">Favorites ({favorites.length})</h2>
        {favorites.length > 0 ? (
          <div className="space-y-2">
            {favorites.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-[var(--color-warning)]/10 rounded-md border border-[var(--color-border)]">
                <div>
                  <div className="font-medium text-[var(--color-surface-text)]">{item.display_name}</div>
                  <div className="text-sm text-[var(--color-secondary-text)]">{item.route_path}</div>
                  <div className="text-xs text-[var(--color-secondary-text)] opacity-75">Type: {item.item_type}</div>
                </div>
                <button
                  onClick={() => handleRemoveFavorite(item.id)}
                  className="px-3 py-1 bg-[var(--color-error)] text-[var(--color-accent-text)] rounded text-sm hover:opacity-90"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No favorites yet. Add some by searching and clicking &quot;Add to Favorites&quot;.</p>
        )}
      </div>

      {/* Recent Items Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Recent Items ({recentItems.length})</h2>
        {recentItems.length > 0 ? (
          <div className="space-y-2">
            {recentItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
                <div>
                  <div className="font-medium">{item.display_name}</div>
                  <div className="text-sm text-gray-600">{item.route_path}</div>
                  <div className="text-xs text-gray-500">Type: {item.item_type}</div>
                </div>
                <button
                  onClick={() => handleAddFavorite(item)}
                  className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                >
                  Add to Favorites
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No recent items yet. Navigate to some pages to see them here.</p>
        )}
      </div>

      {/* Test Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Test Actions</h2>
        <div className="space-x-4">
          <button
            onClick={loadNavigationData}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Reload Data'}
          </button>
          <button
            onClick={() => setError(null)}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            Clear Error
          </button>
        </div>
      </div>
    </div>
  );
};
